/*
 * The note behind the entries info icon.
 *
 * One piece of markup, two shapes: a tooltip pinned to the icon, or a modal dialog. The merchant
 * picks per shop in theme settings and the icon carries the answer, so a page can hold badges in
 * a price row and a cart at once without either knowing about the other.
 *
 * The note ships as an inert <template> next to its icon — a real element there would land on the
 * price row's flex line — and is cloned into the body when it opens.
 */
if (!window.vpEntriesNote) {
  window.vpEntriesNote = true;

  const GAP = 8;
  let tip = null;
  let tipButton = null;
  let dialog = null;

  const noteFor = (button) => {
    const template = button.nextElementSibling;
    if (!template || template.tagName !== 'TEMPLATE') return null;
    const note = template.content.firstElementChild;
    return note ? note.cloneNode(true) : null;
  };

  const closeTip = () => {
    if (!tip) return;
    tip.remove();
    if (tipButton) tipButton.setAttribute('aria-expanded', 'false');
    tip = null;
    tipButton = null;
  };

  /* Centre it under the icon, then pull it back inside the viewport rather than let it run off —
     the badge sits near the right edge of a cart drawer as often as mid-page. */
  const place = (button) => {
    const anchor = button.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;

    let left = anchor.left + anchor.width / 2 - width / 2;
    left = Math.min(Math.max(GAP, left), window.innerWidth - width - GAP);

    let top = anchor.bottom + GAP;
    if (top + height > window.innerHeight - GAP) {
      top = Math.max(GAP, anchor.top - height - GAP);
    }

    /* Everything above is in viewport coordinates, which is what `position: fixed` normally wants.
       It only wants that while the tip's ancestors are untransformed: a transformed ancestor
       becomes the containing block for fixed descendants, and the cart drawer animates its dialog
       with one. Hosted in there, the figures have to be rebased onto the host or the tip is placed
       the whole width of the drawer away and lands off screen. */
    const host = tip.parentElement;
    if (host && host !== document.body) {
      const hostRect = host.getBoundingClientRect();
      left -= hostRect.left;
      top -= hostRect.top;
    }

    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  };

  /* A modal dialog paints in the browser's top layer, which sits above the whole normal stacking
     context no matter what z-index anything else carries. The cart drawer is one, so a tip parked
     on `body` is created correctly and then rendered behind the drawer: the badge looks dead when
     it is really just underneath. Hosting it in the open modal instead puts it in the same layer.
     `position: fixed` still resolves against the viewport there, so `place` needs no change. */
  const tipHost = (button) => button.closest('dialog:modal') || document.querySelector('dialog:modal') || document.body;

  const openTip = (button) => {
    const note = noteFor(button);
    if (!note) return;
    closeTip();
    note.classList.add('vp-entries__note--tip');
    note.setAttribute('role', 'tooltip');
    tipHost(button).appendChild(note);
    tip = note;
    tipButton = button;
    button.setAttribute('aria-expanded', 'true');
    place(button);
  };

  const openDialog = (button) => {
    const note = noteFor(button);
    if (!note) return;

    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'vp-entries__dialog';
      /* A click on the backdrop lands on the dialog itself, never on its contents. */
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      document.body.appendChild(dialog);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vp-entries__note-close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => dialog.close());
    note.appendChild(close);

    note.classList.add('vp-entries__note--dialog');
    dialog.replaceChildren(note);
    dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.vp-entries__info');

    if (button) {
      /* The badge is dropped into rows that are themselves links or click targets. */
      event.preventDefault();
      event.stopPropagation();
      if (button === tipButton) {
        closeTip();
      } else if (button.dataset.noteStyle === 'dialog') {
        openDialog(button);
      } else {
        openTip(button);
      }
      return;
    }

    if (tip && !event.target.closest('.vp-entries__note')) closeTip();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeTip();
  });

  /* `body` is this theme's scroller, so the capture phase is what hears it move. */
  window.addEventListener('scroll', () => {
    if (tipButton) place(tipButton);
  }, true);

  window.addEventListener('resize', closeTip);
}
